import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { useAuth } from '../../hooks/useAuth';
import { useSecureService } from '../../store/service';
import footerStyles from './FooterActions.module.scss';
import StatusChangeModal from '../../modals/statusChangeModal';
import ApprovalModal from '../../modals/approvalModal';
import DeleteModal from '../../components/DeleteModal/DeleteModal';
import ShareModal from '../../components/ShareModal/ShareModal';
import CompareModal from '../../components/CompareModal/CompareModal';
import AddNewFileModal from '../../modals/addNewFileModal';
import AddNewFolderModal from '../../modals/addNewFolderModal';
import AddNewProjectModal from '../../modals/addNewProjectModal';
import StatusSelector from '../../components/StatusSelector/StatusSelector';
import ActionButtons from '../../components/ActionButtons/ActionButtons';

interface FooterActionsProps {
  onCompare?: () => void;
  onDelete?: () => void;
  onDownload?: () => void;
  onCopyUrl?: () => void;
  onStatusChange?: () => void;
  onApprovalRequest?: () => void; // Approval modal'ı açma fonksiyonu
  onEdit?: () => void; // Edit modal'ı açma fonksiyonu
  fileDetail?: any;
  selectedItems?: Set<number>;
  selectedItemsDetails?: Array<{ id: number; name: string; entryType: string; status?: string; share?: any[]; thumbnail?: any }>;
  currentEntry?: any;
  style?: React.CSSProperties;
  selectedVersion?: number; // Mevcut seçili versiyon
  hideDownload?: boolean; // Download butonunu gizleme seçeneği
  isHeaderless?: boolean; // Headerless layout kullanımı için
}

const FooterActions: React.FC<FooterActionsProps> = ({ onCompare, onDelete, onDownload, onCopyUrl, onStatusChange, onApprovalRequest, fileDetail, selectedItems, selectedItemsDetails, currentEntry, style, selectedVersion, hideDownload, isHeaderless }) => {
  const { user, canDelete } = useAuth();
  
  // Sidebar durumunu takip etmek için state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Sidebar durumunu tespit et
  useEffect(() => {
    const checkSidebarState = () => {
      // Önce localStorage'dan oku
      const saved = localStorage.getItem('sidebarCollapsed');
      
      if (saved !== null) {
        const isCollapsed = JSON.parse(saved);
        setIsSidebarCollapsed(isCollapsed);
        return;
      }
      
      // Fallback olarak DOM'dan kontrol et
      const siderElement = document.querySelector('.ant-layout-sider');
      if (siderElement) {
        const hasCollapsedClass = siderElement.classList.contains('ant-layout-sider-collapsed');
        const elementWidth = (siderElement as HTMLElement).offsetWidth;
        const isCollapsed = hasCollapsedClass || elementWidth <= 80;
        
        setIsSidebarCollapsed(isCollapsed);
        
        // localStorage'a kaydet
        localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
      } else {
        // Default olarak collapsed kabul et
        setIsSidebarCollapsed(true);
      }
    };

    // İlk kontrol
    checkSidebarState();
    
    // Biraz gecikme ile tekrar kontrol et (sayfa yüklenme için)
    setTimeout(checkSidebarState, 200);
    setTimeout(checkSidebarState, 500);

    // localStorage değişikliklerini dinle
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'sidebarCollapsed') {
        checkSidebarState();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Interval ile sürekli kontrol et
    const interval = setInterval(checkSidebarState, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Mobil durumu kontrol et
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // İlk kontrol
    checkMobile();

    // Resize event listener
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Guest ve BrandAccountViewer için share yetkisi kontrolü
  const canShare = (): boolean => {
    if (!user) return false;
    return !['Guest', 'BrandAccountViewer'].includes(user.role);
  };

  // Edit yetkisi kontrolü
  const canEdit = (): boolean => {
    // FileVersions ve FileDetail sayfalarında edit butonunu gösterme
    if (window.location.pathname.includes('/versions') || 
        window.location.pathname.includes('/file/') || 
        window.location.pathname.includes('/file-detail/') ||
        fileDetail) { // fileDetail prop'u varsa edit butonunu gizle
      return false;
    }
    
    if (!user) return false;
    return ['Admin', 'SuperAdmin', 'AccountOwner'].includes(user.role);
  };

  // Toplu seçim yetkisi kontrolü - sadece admin, super admin ve account owner
  const canMultiSelect = () => {
    if (!user) return false;
    return ['Admin', 'SuperAdmin', 'AccountOwner'].includes(user.role);
  };

  // Download butonunu sadece File entryType için göster
  const canDownload = (): boolean => {
    // Seçili öğe yoksa false döndür (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      return false;
    }
    
    // FileVersions sayfası için her zaman göster (koşulsuz)
    if (window.location.pathname.includes('/versions')) {
      return true;
    }
    
    // FileDetail sayfası için: seçili versiyonun fileUrl'i varsa göster
    if (fileDetail && selectedVersion) {
      const selectedVersionData = fileDetail.versions?.find((v: any) => v.id === selectedVersion);
      if (selectedVersionData?.fileUrl) {
        return true;
      }
    }
    
    if (selectedItems && selectedItems.size > 0) {
      // Seçili öğelerden herhangi biri File ise göster
      return selectedItemsDetails?.some((item: any) => item.entryType === 'File') || false;
    } else if (fileDetail) {
      return fileDetail.entryType === 'File';
    } else if (currentEntry) {
      return currentEntry.entryType === 'File';
    }
    return false;
  };

  const { getFileById, changeStatus, editFile, editFolder, editProject, resolveFileComment } = useSecureService();
  const [selectedFileDetails, setSelectedFileDetails] = useState<any>(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [selectedStatusForModal, setSelectedStatusForModal] = useState<string>('NeedsReview');
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  
  // Share modal state
  const [isShareModalVisible, setIsShareModalVisible] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);

  // Compare modal state'leri
  const [isCompareModalVisible, setIsCompareModalVisible] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);

  // Edit modal state'leri
  const [isEditFileModalVisible, setIsEditFileModalVisible] = useState(false);
  const [isEditFolderModalVisible, setIsEditFolderModalVisible] = useState(false);
  const [isEditProjectModalVisible, setIsEditProjectModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Compare yetkisi kontrolü - ProjectDetail için fileDetail null olabilir
  const canCompare = () => {
    // Seçili öğe yoksa false döndür (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      return false;
    }
    
    if (!fileDetail) {
      return false; // ProjectDetail'da compare yok
    }
    
    // FileVersions sayfasında compare gösterme
    if (window.location.pathname.includes('/versions')) {
      return false;
    }
    
    if (!fileDetail?.versions || fileDetail.versions.length <= 1) { 
      return false; // Single version
    }
    
    if (!user) { 
      return false; // Anonymous users
    }
    
    if (user.role === 'Guest') { 
      // Guest users can compare - temporarily allow all
      return true;
    }
    
    // Other roles can compare
    return ['AccountManager', 'SuperAdmin', 'AccountOwner', 'InternalTeam', 'BrandAccountViewer'].includes(user.role);
  };

  // Compare modal açma fonksiyonu
  const handleCompareClick = () => {
    // Seçili öğe yoksa işlem yapma (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    setIsCompareModalVisible(true);
  };

  // Compare modal onaylama
  const handleCompareConfirm = (selectedVersionId: number) => {
    setCompareLoading(true);
    
    // Global state'e seçilen versiyonu kaydet
    (window as any).selectedVersionForCompare = selectedVersionId;
    
    // Compare callback'ini çağır
    if (onCompare) {
      onCompare();
    }
    
    // Modal'ı kapat ve loading'i durdur
    setIsCompareModalVisible(false);
    setCompareLoading(false);
  };

  // Compare modal iptal
  const handleCompareCancel = () => {
    setIsCompareModalVisible(false);
  };

  const handleDeleteClick = () => {
    // Seçili öğe yoksa işlem yapma (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    if (!canDelete) {
      message.error('You do not have permission for this action.');
      return;
    }
    setIsDeleteModalVisible(true);
  };

  const handleEditClick = () => {
    // Seçili öğe yoksa işlem yapma
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    if (!canEdit()) {
      message.error('You do not have permission for this action.');
      return;
    }



    // Seçili öğenin tipine göre ilgili modal'ı aç
    const firstSelectedItem = selectedItemsDetails?.[0];
    if (firstSelectedItem) {
      if (firstSelectedItem.entryType === 'File') {
        setIsEditFileModalVisible(true);
      } else if (firstSelectedItem.entryType === 'Folder') {
        setIsEditFolderModalVisible(true);
      } else if (firstSelectedItem.entryType === 'Project') {
        setIsEditProjectModalVisible(true);
      }
    }
  };



  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      if (onDelete) {
        await onDelete();
      }
      setIsDeleteModalVisible(false);
      // Success mesajı onDelete fonksiyonunda handle edilecek
    } catch (error) {
      message.error('An error occurred during deletion.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
  };

  // Statü değiştirme yetkisi kontrolü
  const canChangeStatus = () => {
    // Seçili öğe yoksa false döndür (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      return false;
    }
    
    if (!user) return false;
    
    // AccountOwner, SuperAdmin, AgencyAccountViewer ve BrandAccountViewer statü değiştirebilir
    return ['AccountOwner', 'SuperAdmin', 'InternalTeam', 'BrandAccountViewer','AccountManager'].includes(user.role);
  };

  // Mevcut statüyü al
  const getCurrentStatus = () => {
    if (selectedItems && selectedItems.size > 0) {
      // Seçili öğelerin statüsü - ilk öğenin statüsünü al
      const firstSelectedItem = selectedItemsDetails?.[0];
      return firstSelectedItem?.status || 'NeedsReview';
    } else if (fileDetail) {
      // FileDetail için seçili versiyonun statüsünü al
      if (selectedVersion && fileDetail.versions) {
        const selectedVersionData = fileDetail.versions.find((v: any) => v.id === selectedVersion);
        if (selectedVersionData) {
          return selectedVersionData.status || fileDetail.status || 'NeedsReview';
        }
      }
      return fileDetail.status || 'NeedsReview';
    } else if (currentEntry) {
      return currentEntry.status || 'NeedsReview';
    }
    return 'NeedsReview';
  };



  // Seçili dosyalar için fileDetail yükle
  const loadSelectedFileDetails = async () => {
    // FileDetail sayfasında zaten fileDetail prop'u var, gereksiz API çağrısı yapma
    if (fileDetail) {
      setSelectedFileDetails(fileDetail);
      return;
    }
    
    if (selectedItems && selectedItems.size > 0 && selectedItemsDetails) {
      const fileItems = selectedItemsDetails.filter((item: any) => item.entryType === 'File');
      if (fileItems.length > 0) {
        try {
          const fileId = fileItems[0].id; // İlk dosyayı al
          const response = await getFileById(fileId);
          if (response.success) {
            setSelectedFileDetails(response.data);
          }
        } catch (error) {
          // Error loading selected file details
        }
      }
    }
  };

  // Seçili öğeler değiştiğinde fileDetail yükle
  useEffect(() => {
    loadSelectedFileDetails();
  }, [selectedItems, selectedItemsDetails, fileDetail]);

  // Alt öğesi olup olmadığını kontrol et
  const hasSubentries = () => {
    if (selectedItems && selectedItems.size > 0) {
      // Seçili öğelerden herhangi birinin alt öğesi var mı kontrol et
      const result = selectedItemsDetails?.some((item: any) => {
        // Version seçiliyse alt öğesi yok
        if (item.entryType === 'Version') {
          return false;
        }
        
        // Project seçiliyse her zaman alt öğesi olabilir (Projects sayfası)
        if (item.entryType === 'Project') {
          return true;
        }
        
        // Folder seçiliyse, ProjectDetail'da children kontrolü yap
        if (item.entryType === 'Folder') {
          // ProjectDetail'da children var mı kontrol et
          if (currentEntry?.children && Array.isArray(currentEntry.children)) {
            return currentEntry.children.length > 0;
          }
          return true; // Varsayılan olarak folder'ların alt öğesi olabilir
        }
        
        // File seçiliyse
        if (item.entryType === 'File') {
          // FileVersions sayfasında fileDetail'dan versions kontrolü
          if (fileDetail?.versions && Array.isArray(fileDetail.versions)) {
            return fileDetail.versions.length > 0;
          }
          // ProjectDetail'da file seçiliyse varsayılan olarak true
          return true;
        }
        
        return false;
      }) || false;
      
      return result;
    } else if (fileDetail) {
      // FileVersions/FileDetail sayfası - dosya için versiyonlar var mı kontrol et
      return fileDetail.versions && Array.isArray(fileDetail.versions) && fileDetail.versions.length > 0;
    } else if (currentEntry) {
      // ProjectDetail sayfası - klasör/proje için children var mı kontrol et
      return currentEntry.children && Array.isArray(currentEntry.children) && currentEntry.children.length > 0;
    }
    return false;
  };




  // Brand rolü için onay süreci
    const handleApprovalProcess = (newStatus: string, unresolvedComments: number) => {
    if (newStatus === 'Approved') {
      // Approval modal'ını aç
      setIsApprovalModalVisible(true);
    } else {
      // Updated için direkt statü değiştir
      performStatusChange('Updated', false);
    }
  };

  const handleApprovalConfirm = async () => {
    setApprovalLoading(true);
    try {
      // Statü değişikliğini yap
      await performStatusChange('Approved', false);
      
      // Modal'ı kapat
      setIsApprovalModalVisible(false);
    } catch (error) {
      message.error('Failed to change status. Please try again.');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleApprovalCancel = () => {
    setIsApprovalModalVisible(false);
  };

    const handleStatusConfirm = async (newStatus: string, changeSubentries: boolean) => {
    // Normal statü değişikliği (Brand rolü artık buraya gelmez)
    await performStatusChange(newStatus, changeSubentries);
  };

  // Statü değişikliği işlemini ayrı fonksiyon olarak çıkar
  const performStatusChange = async (newStatus: string, changeSubentries: boolean) => {
    setStatusLoading(true);
    try {
      const entries = [];
      
      if (selectedItems && selectedItems.size > 0) {
        // Seçili öğelerin statüsünü değiştir
        selectedItemsDetails?.forEach((item: any) => {
          entries.push({
            id: item.id,
            entryType: item.entryType,
            entryStatus: newStatus,
            changeSubentries: changeSubentries
          });
        });
      } else if (fileDetail) {
        // FileDetail sayfasında selectedVersion ID'sini kullan
        const versionId = selectedVersion || fileDetail.id;
        entries.push({
          id: versionId,
          entryType: selectedVersion ? 'Version' : (fileDetail.entryType || 'File'),
          entryStatus: newStatus,
          changeSubentries: changeSubentries
        });
      } else if (currentEntry) {
        // Klasör/Proje statüsünü değiştir
        entries.push({
          id: currentEntry.id,
          entryType: currentEntry.entryType,
          entryStatus: newStatus,
          changeSubentries: changeSubentries
        });
      }

      if (entries.length > 0) {
        const response = await changeStatus(entries);
        if (response.success) {
          message.success('Status changed successfully.');
          setIsStatusModalVisible(false);
          // Callback ile parent component'i bilgilendir
          if (onStatusChange) {
            onStatusChange();
          }
        } else {
          // Unauthorized hatası için özel mesaj
          if (response.message === 'Unauthorized') {
            message.error('You do not have permission to change status for these items.');
          } else {
            throw new Error(response.message || 'Status change failed');
          }
        }
      }
    } catch (error) {
      // Network hatası veya diğer hatalar için genel mesaj
      message.error('Network error or insufficient permissions. Please try again.');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleStatusCancel = () => {
    setIsStatusModalVisible(false);
  };

  // Share functions
  const handleShareClick = () => {
    // Seçili öğe yoksa işlem yapma (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    setIsShareModalVisible(true);
  };

  const handleShareConfirm = async (shareList: string[]) => {
    setShareLoading(true);
    try {
      let result;
      
      if (selectedItems && selectedItems.size > 0) {
        // Update share for selected items - currently only first item
        const firstSelectedItem = selectedItemsDetails?.[0];
        if (firstSelectedItem) {
          const editData = {
            id: firstSelectedItem.id,
            name: firstSelectedItem.name,
            thumbnail: firstSelectedItem.thumbnail || null,
            share: shareList
          };
          
          if (firstSelectedItem.entryType === 'File') {
            result = await editFile(editData);
          } else if (firstSelectedItem.entryType === 'Folder') {
            result = await editFolder(editData);
          } else if (firstSelectedItem.entryType === 'Project') {
            result = await editProject(editData);
          } else if (firstSelectedItem.entryType === 'Version') {
            // Versiyon paylaşımları dosya seviyesinde yönetilir; mümkünse fileDetail.id ile güncelle
            const fileId = fileDetail?.id || firstSelectedItem.id;
            const fileName = fileDetail?.name || firstSelectedItem.name;
            const fileThumb = (fileDetail?.thumbnail || firstSelectedItem.thumbnail) || null;
            result = await editFile({ id: fileId, name: fileName, thumbnail: fileThumb, share: shareList });
          }
        }
      } else if (fileDetail) {
        // Update file share
        const editData = {
          id: fileDetail.id,
          name: fileDetail.name,
          thumbnail: fileDetail.thumbnail || null,
          share: shareList
        };
        result = await editFile(editData);
      } else if (currentEntry) {
        // Update folder/project share
        const editData = {
          id: currentEntry.id,
          name: currentEntry.name,
          thumbnail: currentEntry.thumbnail || null,
          share: shareList
        };
        
        if (currentEntry.entryType === 'Folder') {
          result = await editFolder(editData);
        } else if (currentEntry.entryType === 'Project') {
          result = await editProject(editData);
        }
      }

      if (result && result.success) {
        message.success('Share list updated successfully.');
        setIsShareModalVisible(false);
        // Notify parent component
        if (onStatusChange) {
          onStatusChange();
        }
      } else {
        throw new Error(result?.message || 'Share update failed');
      }
    } catch (error) {
      message.error('An error occurred while updating share.');
    } finally {
      setShareLoading(false);
    }
  };

  const handleShareCancel = () => {
    setIsShareModalVisible(false);
  };

  const handleDownloadClick = async () => {
    // Seçili öğe yoksa işlem yapma (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    if (!onDownload) {
      message.error('Download function not available.');
      return;
    }
    
    setDownloadLoading(true);
    try {
      await onDownload();
    } catch (error) {
      message.error('An error occurred during download.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    // Seçili öğe yoksa işlem yapma (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    if (!canChangeStatus()) {
      message.error('You do not have permission for this action.');
      return;
    }

    const currentStatus = getCurrentStatus();

    // Approved seçen tüm roller için özel kontrol - her zaman modal aç
    if (newStatus === 'Approved') {
      // FileDetail sayfasında mıyız kontrol et
      const isFilePage = fileDetail || (selectedItemsDetails && selectedItemsDetails.length > 0 && selectedItemsDetails[0]?.entryType === 'Version');
      
      if (isFilePage) {
        // Approved seçildiğinde resolve kontrolü yap
        const unresolvedComments = countUnresolvedComments();
        handleApprovalProcess(newStatus, unresolvedComments);
        return;
      }
      
      // ProjectDetail sayfasında da approval modal'ı aç (sadece fileDetail yoksa)
      if (currentEntry && !fileDetail && onApprovalRequest) {
        onApprovalRequest(); // ProjectDetail'ın approval modal'ını aç
        return;
      }
      
      // Projects sayfasında da approval modal'ı aç (selectedItemsDetails varsa ve onApprovalRequest varsa)
      if (selectedItemsDetails && selectedItemsDetails.length > 0 && onApprovalRequest && !fileDetail && !currentEntry) {
        onApprovalRequest(); // Projects'ın approval modal'ını aç
        return;
      }
    }

    // Diğer roller için normal modal
    setSelectedStatusForModal(newStatus);
    setIsStatusModalVisible(true);
  };

  // Resolve edilmemiş comment sayısını hesapla
  const countUnresolvedComments = () => {
    let unresolvedComments = 0;
    
    if (fileDetail?.versions) {
      fileDetail.versions.forEach((version: any) => {
        if (version.comments && Array.isArray(version.comments)) {
          version.comments.forEach((comment: any) => {
            if (!comment.isResolved) {
              unresolvedComments++;
            }
          });
        }
      });
    }
    
    return unresolvedComments;
  };

  return (
    <>
      <div className={ isHeaderless ? footerStyles.headerlessFooterActions : footerStyles.footerActions} style={{
        ...style,
        left: isMobile ? '0px' : (isSidebarCollapsed ? '60px' : '270px'),
        width: isMobile ? '100%' : (isSidebarCollapsed ? 'calc(100% - 60px)' : 'calc(100% - 270px)'),
        transition: 'all 0.2s ease-in-out',
        justifyContent: onStatusChange ? 'space-between' : 'flex-end'
      }}>
        {onStatusChange && (
          <StatusSelector
            onChange={handleStatusChange}
            disabled={!selectedItems || selectedItems.size === 0}
            userRole={user?.role}
            selectedItems={selectedItems}
            selectedItemsDetails={selectedItemsDetails}
            fileDetail={fileDetail}
            currentEntry={currentEntry}
            selectedVersion={selectedVersion}
            canMultiSelect={canMultiSelect}
          />
        )}
        
        <ActionButtons
          canDelete={canDelete}
          canShare={canShare}
          canDownload={canDownload}
          canCompare={canCompare}
          canEdit={canEdit}
          canMultiSelect={canMultiSelect}
          selectedItems={selectedItems}
          fileDetail={fileDetail}
          currentEntry={currentEntry}
          onDelete={handleDeleteClick}
          onCompare={handleCompareClick}
          onCopyUrl={onCopyUrl}
          onDownload={handleDownloadClick}
          onShare={handleShareClick}
          onEdit={handleEditClick}
          hideDownload={hideDownload}
          downloadLoading={downloadLoading}
        />
      </div>

      <DeleteModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={deleteLoading}
        selectedItems={selectedItems}
        selectedItemsDetails={selectedItemsDetails}
        fileDetail={fileDetail}
        currentEntry={currentEntry}
      />

      {/* Status Change Modal */}
      <StatusChangeModal
        visible={isStatusModalVisible}
        onCancel={handleStatusCancel}
        onConfirm={handleStatusConfirm}
        currentStatus={getCurrentStatus()}
        hasSubentries={hasSubentries()}
        loading={statusLoading}
        selectedStatus={selectedStatusForModal}
      />

      <ApprovalModal
        visible={isApprovalModalVisible}
        onCancel={handleApprovalCancel}
        onConfirm={handleApprovalConfirm}
        loading={approvalLoading}
      />

      {/* Share Modal */}
      <ShareModal
        visible={isShareModalVisible}
        onCancel={handleShareCancel}
        onConfirm={handleShareConfirm}
        loading={shareLoading}
        selectedItems={selectedItems}
        selectedItemsDetails={selectedItemsDetails}
        fileDetail={fileDetail}
        currentEntry={currentEntry}
      />

      {/* Compare Modal */}
      <CompareModal
        visible={isCompareModalVisible}
        onCancel={handleCompareCancel}
        onConfirm={handleCompareConfirm}
        loading={compareLoading}
        fileDetail={fileDetail}
        selectedVersion={selectedVersion}
      />

      {/* Edit File Modal */}
      <AddNewFileModal
        isOpen={isEditFileModalVisible}
        onClose={() => setIsEditFileModalVisible(false)}
        onSuccess={() => {
          setIsEditFileModalVisible(false);
          if (onStatusChange) {
            onStatusChange();
          }
        }}
        isFromFileDetail={false}
        editMode={true}
        editData={(() => {
          // Seçili versiyonun bilgilerini al
          const selectedItem = selectedItemsDetails?.[0];
          if (selectedItem) {
            return {
              ...selectedItem,
              share: selectedItem.share || []
            };
          }
          // Fallback olarak fileDetail kullan
          return {
            ...(fileDetail || {}),
            share: fileDetail?.share || []
          };
        })()}
      />

      {/* Edit Folder Modal */}
      <AddNewFolderModal
        isOpen={isEditFolderModalVisible}
        onClose={() => setIsEditFolderModalVisible(false)}
        onSuccess={() => {
          setIsEditFolderModalVisible(false);
          if (onStatusChange) {
            onStatusChange();
          }
        }}
        editMode={true}
        editData={{
          ...(currentEntry || selectedItemsDetails?.[0]),
          share: currentEntry?.share || selectedItemsDetails?.[0]?.share || []
        }}
      />

      {/* Edit Project Modal */}
      <AddNewProjectModal
        isOpen={isEditProjectModalVisible}
        onClose={() => setIsEditProjectModalVisible(false)}
        onSuccess={() => {
          setIsEditProjectModalVisible(false);
          if (onStatusChange) {
            onStatusChange();
          }
        }}
        editMode={true}
        editData={{
          ...(currentEntry || selectedItemsDetails?.[0]),
          share: currentEntry?.share || selectedItemsDetails?.[0]?.share || []
        }}
      />
    </>
  );
};

export default FooterActions; 